import { css } from '@emotion/core';
import styled from '@emotion/styled';
import React, { useState } from 'react';
import sizes from 'style/sizes';
import { baseTransitionSecond, breakPointSP, easeOut, grayscaleFilterSimple, mq } from 'style/variables';
import { Caption } from '../caption';
import { InView } from 'react-intersection-observer';
import { useIsSp } from 'util/useIsSp';
import Gradient from 'assets/images/common/gradient.png';
import GrayCoverFV from 'components/atoms/FV/GrayCoverFV';
import fonts from 'style/fonts';
import { default as color } from 'style/colors';

type ImgProps = {
  filename: string | string[]; // 画像のURL（配列の場合はレスポンシブ対応）
  maskImage?: string[]; // 0=sp, 1=pc
  alt: string;
  caption?: string;
  spFullSize?: boolean;
  isContain?: boolean;
  isHovered?: boolean;
  rendered?: boolean;
  noTriggerInView?: boolean;
  loading?: 'lazy' | 'eager';
};

// StaticImgは実際のimg要素のプロップスのみを受け取る
type StaticImgProps = {
  spFullSize?: boolean;
  isContain?: boolean;
  rendered?: boolean;
};

const LoadingOverlay = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ show }) => (show ? '1' : '0')};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  & > span {
    z-index: 1;
    width: max-content;
    height: 1.5rem;
    font-family: ${fonts.enGothic};
    font-size: 1.2rem;
    line-height: 1;
    letter-spacing: 1px;
    color: ${color.gray07};
    animation: flash 1.6s ease-out infinite;
    @keyframes flash {
      0% {
        opacity: 0;
      }
      50% {
        opacity: 1;
      }
      100% {
        opacity: 0;
      }
    }
  }
`;

const StaticImg = styled('img')<StaticImgProps>`
  width: 100%;
  height: 100%;
  vertical-align: bottom;
  object-fit: cover;
  object-position: center center;
  opacity: ${({ rendered }) => (rendered ? '1' : '0')};
  transition: opacity 0.3s ease-in-out;

  ${mq.onlysp} {
    ${props =>
      props.spFullSize &&
      css`
        margin: 0 -${sizes.margin[32]};
        width: 100vw;
      `}
  }

  // オブジェクトフィット対応
  ${props =>
    props.isContain &&
    css`
      object-fit: contain;
    `}
`;

const ImageCaption = styled(Caption)`
  margin-top: ${sizes.margin[32]};
  ${mq.onlysp} {
    margin-top: ${sizes.margin[17]};
  }
`;

const Wrapper = styled.div<{ isHovered?: boolean }>`
  width: 100%;

  ${({ isHovered }) =>
    isHovered &&
    css`
      filter: none;
    `}
`;

const FV: React.FC<ImgProps> = props => {
  const [isHovered, setIsHovered] = useState(false);
  const isSp = useIsSp(); // 強制trigger in view
  const isTriggerInView = isSp || !props.noTriggerInView;

  const hasMaskImage = props.maskImage !== undefined && props.maskImage !== null && props.maskImage.length > 0;

  if (isTriggerInView) {
    return (
      <InView onChange={inView => setIsHovered(inView)} threshold={0.5} delay={200}>
        {({ ref }) => (
          <Wrapper
            isHovered={isHovered}
            onMouseEnter={() => !isTriggerInView && setIsHovered(true)}
            onMouseLeave={() => !isTriggerInView && setIsHovered(false)}
            ref={ref}
          >
            <ImgBaseExtends {...props} hasMaskImage={hasMaskImage} isHovered={isHovered} />
          </Wrapper>
        )}
      </InView>
    );
  }

  return (
    <Wrapper
      isHovered={isHovered}
      onMouseEnter={() => !isTriggerInView && setIsHovered(true)}
      onMouseLeave={() => !isTriggerInView && setIsHovered(false)}
    >
      <ImgBaseExtends {...props} hasMaskImage={hasMaskImage} isHovered={isHovered} />
    </Wrapper>
  );
};

type ImgBaseProps = ImgProps & { className?: string };
const ImgBase: React.FC<ImgBaseProps> = props => {
  const loading = props.loading ?? 'lazy';
  const [rendered, setRendered] = useState(loading !== 'eager');
  const isSP = useIsSp();

  const handleImageLoad = () => {
    if (loading === 'eager') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setRendered(true);
          }, 300);
        });
      });
    } else {
      setRendered(true);
    }
  };

  // シンプルな画像URLの処理
  if (typeof props.filename === 'string') {
    return (
      <div className={props.className} style={{ position: 'relative' }}>
        <LoadingOverlay show={!rendered} />
        <picture>
          <StaticImg
            src={`https://flamboyant-hypatia2860.on.getshifter.io${props.filename}`}
            {...props}
            alt={props.alt}
            className={'a-image'}
            spFullSize={props.spFullSize}
            isContain={props.isContain}
            loading={loading}
            onLoad={handleImageLoad}
            rendered={rendered}
          />
        </picture>
        {props.maskImage && (
          <>
            <DotLikeCover isHovered={props.isHovered} rendered={rendered} />
            <GrayCoverFV
              rendered={rendered}
              isHovered={props.isHovered}
              maskImage={`https://flamboyant-hypatia2860.on.getshifter.io${props.maskImage[0]}`}
            />
          </>
        )}
        {props.caption && <ImageCaption>{props.caption}</ImageCaption>}
      </div>
    );
  }

  // 配列の場合（レスポンシブ対応）
  if (Array.isArray(props.filename)) {
    return (
      <div className={props.className} style={{ position: 'relative' }}>
        <LoadingOverlay show={!rendered} />
        <picture>
          <source
            media={`(max-width: ${breakPointSP}px)`}
            srcSet={`https://flamboyant-hypatia2860.on.getshifter.io${props.filename[0]}`}
          />
          <StaticImg
            src={`https://flamboyant-hypatia2860.on.getshifter.io${props.filename[1]}`}
            alt={props.alt}
            spFullSize={props.spFullSize}
            isContain={props.isContain}
            className="a-image"
            loading={loading}
            onLoad={handleImageLoad}
            rendered={rendered}
          />
        </picture>
        {props.maskImage && (
          <>
            <DotLikeCover isHovered={props.isHovered} rendered={rendered} />
            <GrayCoverFV
              rendered={rendered}
              isHovered={props.isHovered}
              maskImage={
                isSP
                  ? `https://flamboyant-hypatia2860.on.getshifter.io${props.maskImage[0]}`
                  : `https://flamboyant-hypatia2860.on.getshifter.io${props.maskImage[1]}`
              }
            />
          </>
        )}
        {props.caption && <ImageCaption>{props.caption}</ImageCaption>}
      </div>
    );
  }

  return null;
};

const DotLikeCover = styled('div')<{ isHovered?: boolean; rendered?: boolean }>`
  position: absolute !important;
  inset: 0;
  background-image: url(${Gradient});
  background-size: 100% 100%;
  mix-blend-mode: color-burn;
  background-position: 0 0;

  @keyframes hover3-reverse {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  animation: hover3-reverse ${baseTransitionSecond * 4}s linear forwards;

  ${({ isHovered, rendered }) =>
    isHovered &&
    rendered &&
    css`
      @keyframes hover3 {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
      animation: hover3 ${baseTransitionSecond * 4}s linear forwards;
      animation-delay: ${baseTransitionSecond * 1}s;
      /* background-position: 10px 0px; */
    `};
`;

// hover演出よベース。まず色が変わる箇所を制御
const ImgBaseExtends = styled(ImgBase)<{
  isHovered?: boolean;
  hasMaskImage?: boolean;
}>`
  position: relative;
  max-width: 1920px;
  margin: 0 auto;
  height: 100%;
  width: auto;
  filter: ${grayscaleFilterSimple};

  @keyframes hover2-reverse {
    0% {
      filter: none;
    }
    100% {
      filter: ${grayscaleFilterSimple};
    }
  }
  animation: hover2-reverse ${baseTransitionSecond * 4}s ${easeOut} forwards;

  ${({ isHovered }) =>
    isHovered &&
    css`
      @keyframes hover2 {
        0% {
          filter: ${grayscaleFilterSimple};
        }
        100% {
          filter: none;
        }
      }
      animation: hover2 ${baseTransitionSecond * 4}s ${easeOut} forwards;
    `};

  ${({ hasMaskImage, isHovered }) =>
    hasMaskImage &&
    isHovered &&
    css`
      animation-delay: ${baseTransitionSecond * 1}s;
    `};
`;

export default FV;
